import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { Banner, Button, Card, Checkbox, DataTable, Dialog, Tooltip, TopAppBar, useSnackbar } from '@meshble/molo';
import type { components } from '../api/schema';
import { api, must } from '../lib/api';
import { AREE, PERMESSI, nomeColonna, titoloDi } from '../lib/permessi';
import { CaricoSezione } from '../brand';
import { BarraUtente } from '../shell';
import { localeIntl, t } from '../lib/lingua';

type Casella = components['schemas']['CasellaJSON'];
type Matrice = components['schemas']['MatriceOutputBody'];
type Cambio = components['schemas']['SetCasellaPermessiRequest'];
type Colonna = Cambio['colonna'];

/** Perché una casella non si cambia. */
function motivoBlocco(b: string): string {
  return b === 'governo'
    ? t('Il proprietario tiene sempre le persone: senza, l’organizzazione resterebbe senza nessuno che la gestisca.')
    : t('Un accesso su un’istanza o un server dà solo permessi su quella risorsa, non sull’organizzazione.');
}

/** Una concessione che chiede conferma: un permesso pericoloso sotto l'admin, o a un cliente. */
function chiedeConferma(colonna: string, permesso: string): boolean {
  return !!PERMESSI[permesso]?.pericoloso && !['owner', 'admin'].includes(colonna);
}

/**
 * Accessi: cosa vede e cosa usa ogni ruolo, in tutte le organizzazioni
 * (blocco 35). Solo per lo staff.
 *
 * Le righe sono permessi con un nome da persona, raggruppati come il
 * pannello; sotto ognuno, cosa apre. La scala resta una scala: concedere a
 * una colonna concede anche a quelle a sinistra, togliere toglie anche a
 * quelle a destra. Lo fa l'hub, e la tabella mostra quello che torna.
 */
export function AccessiPage() {
  const { slug } = useParams({ from: '/shell/orgs/$slug/accessi' });
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const [conferma, setConferma] = useState<{ colonna: Colonna; permesso: string } | null>(null);
  const [daCapo, setDaCapo] = useState(false);

  const q = useQuery({ queryKey: ['matrice'], queryFn: () => must(api.GET('/api/v1/permessi', {})) });
  const aggiorna = (m: Matrice) => {
    qc.setQueryData(['matrice'], m);
    // I permessi di chi guarda possono essere cambiati anche per lui.
    qc.invalidateQueries({ queryKey: ['me'] });
    qc.invalidateQueries({ queryKey: ['permessi'] });
  };
  const cambia = useMutation({
    mutationFn: (b: Cambio) =>
      must(api.PUT('/api/v1/permessi', { body: b })),
    onSuccess: (m, b) => {
      aggiorna(m);
      setConferma(null);
      avvisa(b.concesso
        ? t('«{permesso}» concesso a {colonna}', { permesso: t(titoloDi(b.permesso)), colonna: nomeColonna(b.colonna) })
        : t('«{permesso}» tolto a {colonna}', { permesso: t(titoloDi(b.permesso)), colonna: nomeColonna(b.colonna) }));
    },
  });
  const diSerie = useMutation({
    mutationFn: () => must(api.DELETE('/api/v1/permessi', {})),
    onSuccess: (m) => {
      aggiorna(m);
      setDaCapo(false);
      avvisa(t('Tutti i permessi sono tornati come di serie'));
    },
  });

  const colonne = q.data?.colonne ?? [];
  const caselle = new Map((q.data?.caselle ?? []).map((c) => [`${c.colonna}|${c.permesso}`, c]));
  // Nell'ordine del catalogo, che va da guardare a distruggere; uno che il
  // pannello non conosce va in fondo, con la sua chiave.
  const ordine = Object.keys(PERMESSI);
  const posto = (p: string) => (ordine.includes(p) ? ordine.indexOf(p) : ordine.length);
  const permessi = [...new Set((q.data?.caselle ?? []).map((c) => c.permesso))].sort((a, b) => posto(a) - posto(b));
  const cambiate = (q.data?.caselle ?? []).filter((c) => c.concesso !== c.diSerie).length;

  const clic = (c: Casella, concesso: boolean) => {
    // Le colonne arrivano dall'hub, che è anche chi le valida al ritorno.
    const colonna = c.colonna as Colonna;
    if (concesso && chiedeConferma(colonna, c.permesso)) setConferma({ colonna, permesso: c.permesso });
    else cambia.mutate({ colonna, permesso: c.permesso, concesso });
  };

  const cella = (c: Casella | undefined) => {
    if (!c) return null;
    const box = (
      <Checkbox
        checked={c.concesso}
        disabled={!!c.blocco || cambia.isPending}
        onCheckedChange={(v) => clic(c, v === true)}
        label={<span className="md-solo-lettori">{nomeColonna(c.colonna)}</span>}
      />
    );
    if (c.blocco) {
      return <Tooltip content={motivoBlocco(c.blocco)}><span className="md-vietato" tabIndex={0}>{box}</span></Tooltip>;
    }
    if (c.concesso === c.diSerie) return box;
    const quando = c.cambiatoIl ? new Date(c.cambiatoIl).toLocaleDateString(localeIntl()) : '';
    return (
      <span className="md-accessi__cambiata">
        {box}
        <Tooltip content={t('Di serie: {valore} · cambiato da {chi} il {quando}', {
          valore: c.diSerie ? t('sì') : t('no'), chi: c.cambiatoDa ?? t('qualcuno'), quando,
        })}>
          <span className="md-accessi__segno" tabIndex={0} aria-label={t('Diverso da come nasce')}>●</span>
        </Tooltip>
      </span>
    );
  };

  const colonneTabella = [
    { key: 'permesso', header: t('Permesso') },
    ...colonne.map((c) => ({ key: c, header: nomeColonna(c) })),
  ];

  return (
    <>
      <TopAppBar title={t('Accessi')} actions={<BarraUtente slug={slug} />} />
      <CaricoSezione attiva={q.isPending} />
      <div className="md-content">
        <div className="md-stack">
          <p className="md-body-medium">
            {t('Cosa vede e cosa usa ogni ruolo, in tutte le organizzazioni di questo Molo. Concedere a una colonna concede anche a quelle a sinistra; togliere toglie anche a quelle a destra. Ogni modifica finisce nel registro di ogni organizzazione.')}
          </p>
          {q.error && <Banner tone="error" title={t('Non riesco a leggere la matrice')}>{q.error.message}</Banner>}
          {cambia.error && <Banner tone="error" title={t('Modifica non applicata')}>{cambia.error.message}</Banner>}
          {q.data && (
            <div className="md-stack__inline">
              <span className="md-body-medium">
                {cambiate === 0 ? t('Tutto come di serie.')
                  : cambiate === 1 ? t('Una casella diversa da come Molo nasce.')
                  : t('{n} caselle diverse da come Molo nasce.', { n: cambiate })}
              </span>
              {cambiate > 0 && <Button variant="outlined" onClick={() => setDaCapo(true)}>{t('Torna come di serie')}</Button>}
            </div>
          )}
          {AREE.map((area) => {
            const righe = permessi.filter((p) => (PERMESSI[p]?.area ?? AREE[AREE.length - 1]) === area);
            if (righe.length === 0) return null;
            return (
              <Card key={area}>
                <h2 className="md-title-medium">{t(area)}</h2>
                <DataTable
                  columns={colonneTabella}
                  rowKey={(r) => String(r.chiave)}
                  rows={righe.map((p) => ({
                    chiave: p,
                    permesso: (
                      <div className="md-accessi__permesso">
                        <span className="md-body-large">
                          {t(titoloDi(p))}
                          {PERMESSI[p]?.pericoloso && (
                            <Tooltip content={t(PERMESSI[p]!.pericoloso!)}>
                              <span className="md-accessi__pericolo" tabIndex={0} aria-label={t('Pericoloso')}>!</span>
                            </Tooltip>
                          )}
                        </span>
                        {PERMESSI[p] && <span className="md-body-small">{t(PERMESSI[p]!.apre)}</span>}
                      </div>
                    ),
                    ...Object.fromEntries(colonne.map((c) => [c, cella(caselle.get(`${c}|${p}`))])),
                  }))}
                />
              </Card>
            );
          })}
        </div>
      </div>

      <Dialog
        open={conferma !== null}
        onOpenChange={(v) => !v && setConferma(null)}
        title={t('Concedere «{permesso}» a {colonna}?', {
          permesso: conferma ? t(titoloDi(conferma.permesso)) : '', colonna: conferma ? nomeColonna(conferma.colonna) : '',
        })}
        description={conferma && PERMESSI[conferma.permesso]?.pericoloso ? t(PERMESSI[conferma.permesso]!.pericoloso!) : undefined}
        actions={
          <>
            <Button variant="text" onClick={() => setConferma(null)}>{t('Annulla')}</Button>
            <Button
              variant="danger"
              disabled={cambia.isPending}
              onClick={() => conferma && cambia.mutate({ ...conferma, concesso: true })}
            >
              {t('Concedi')}
            </Button>
          </>
        }
      >
        <p className="md-body-medium">{t('Vale in tutte le organizzazioni, e anche per le colonne a sinistra.')}</p>
      </Dialog>

      <Dialog
        open={daCapo}
        onOpenChange={setDaCapo}
        title={t('Tornare come di serie?')}
        description={t('Ogni casella cambiata torna com’è scritta in Molo, in tutte le organizzazioni.')}
        actions={
          <>
            <Button variant="text" onClick={() => setDaCapo(false)}>{t('Annulla')}</Button>
            <Button variant="danger" disabled={diSerie.isPending} onClick={() => diSerie.mutate()}>{t('Torna come di serie')}</Button>
          </>
        }
      />
    </>
  );
}
